import React from 'react';
import { Navigate } from 'react-router-dom';
import { useSession } from '../app/providers/SessionProvider.jsx';
import { loadMyContractorAccess } from '../services/vimPay/contractorService.js';
import { listMyProjectAssignments } from '../services/vimPay/projectService.js';
import {
  listMyPaymentRequests,
  reservedUnitsForAssignment,
  submitMyPaymentRequest,
} from '../services/vimPay/paymentRequestService.js';
import '../styles/vim-pay-payments.css';

function human(value, fallback = 'Not set') {
  const text = String(value || '').replace(/_/g, ' ').trim();
  return text ? text.replace(/\b\w/g, (letter) => letter.toUpperCase()) : fallback;
}
function money(value) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value || 0));
}
function statusTone(status) {
  if (status === 'ready_for_payment') return 'green';
  if (['rejected', 'changes_requested'].includes(status)) return 'red';
  return 'gold';
}

export function VimPayPaymentsPage() {
  const { ready, user } = useSession();
  const [access, setAccess] = React.useState(null);
  const [assignments, setAssignments] = React.useState([]);
  const [requests, setRequests] = React.useState([]);
  const [configured, setConfigured] = React.useState(true);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [assignmentId, setAssignmentId] = React.useState('');
  const [units, setUnits] = React.useState('');
  const [note, setNote] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!ready || !user?.id) return;
    setLoading(true);
    setError('');
    try {
      const [workerAccess, projectResult, requestResult] = await Promise.all([
        loadMyContractorAccess(user.id),
        listMyProjectAssignments(),
        listMyPaymentRequests(),
      ]);
      setAccess(workerAccess);
      setAssignments(projectResult.assignments || []);
      setRequests(requestResult.requests || []);
      setConfigured(projectResult.configured && requestResult.configured);
    } catch (err) {
      setError(err?.message || 'Unable to load VIM Pay payment requests.');
    } finally {
      setLoading(false);
    }
  }, [ready, user?.id]);

  React.useEffect(() => { load(); }, [load]);

  if (ready && !user) return <Navigate to="/pay/login" replace />;
  if (!ready || loading) return <section className="vim-pay-card vim-pay-center-card"><h1>Loading payment requests…</h1></section>;
  if (error) return <section className="vim-pay-card vim-pay-center-card"><h1>Payment requests unavailable</h1><p>{error}</p></section>;
  if (!access?.authorized) return <Navigate to="/pay" replace />;
  if (!configured) return <section className="vim-pay-card vim-pay-center-card"><h1>VIM Pay payroll is being activated.</h1><p>Your VIM Pay access is valid, but the payment/payroll database contract is not active yet.</p></section>;

  if (access.profile?.engagement_type === 'commission_contractor') {
    const rate = Number(access.profile?.commission_rate_percent || 0);
    const label = Number.isFinite(rate) ? `${rate.toFixed(4).replace(/0+$/,'').replace(/\\.$/,'')}%` : 'Assigned by VIM';
    return <section className="vim-pay-dashboard">
      <div className="vim-pay-dashboard-heading"><div><span className="vim-pay-kicker">COMMISSION CONTRACTOR</span><h1>Commission payments</h1><p>Your commission is calculated by VIM Finance from the approved commissionable amount.</p></div></div>
      <div className="vim-pay-card vim-pay-center-card"><h2>Approved commission rate: {label}</h2><p>You do not submit hours or worked units for this engagement. Finance enters the amount eligible for commission, VIM Pay applies your locked commission percentage, and the calculated payout is routed through the project approval workflow.</p><p>Your W-9 and payout preference must be complete before the payment can be submitted for approval.</p></div>
    </section>;
  }

  const eligible = assignments.filter((assignment) => assignment.status !== 'cancelled');
  const selected = eligible.find((assignment) => assignment.id === assignmentId) || null;
  const reserved = selected ? reservedUnitsForAssignment(requests, selected.id) : 0;
  const remaining = selected ? Math.max(0, Number(selected.authorized_units || 0) - reserved) : 0;
  const requested = Number(units || 0);
  const preview = selected && Number.isFinite(requested) ? Number(selected.rate_amount || 0) * requested : 0;

  async function submit(event) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setMessage('');
    try {
      await submitMyPaymentRequest({ assignmentId, requestedUnits: units, note });
      setUnits('');
      setNote('');
      setAssignmentId('');
      setMessage('Payment request submitted for Production review.');
      await load();
    } catch (err) {
      setError(err?.message || 'Unable to submit payment request.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="vim-pay-dashboard">
      <div className="vim-pay-dashboard-heading">
        <div>
          <span className="vim-pay-kicker">PHASE 5</span>
          <h1>Payments & Payroll</h1>
          <p>Submit actual worked units against a VIM-approved assignment and follow Production, Finance, and payroll-fringe status.</p>
        </div>
      </div>

      <form className="vim-pay-card vim-pay-payment-form" onSubmit={submit}>
        <div><span className="vim-pay-kicker">NEW REQUEST</span><h2>Submit worked units</h2></div>
        <label>Project assignment
          <select value={assignmentId} onChange={(event) => { setAssignmentId(event.target.value); setUnits(''); }} required>
            <option value="">Choose an assignment…</option>
            {eligible.map((assignment) => {
              const used = reservedUnitsForAssignment(requests, assignment.id);
              const left = Math.max(0, Number(assignment.authorized_units || 0) - used);
              return <option key={assignment.id} value={assignment.id} disabled={left <= 0}>{assignment.project?.name || 'Project'} — {assignment.role_name} — {left} {assignment.rate_unit}(s) remaining</option>;
            })}
          </select>
        </label>
        {selected ? <div className="vim-pay-request-preview">
          <div><span>Approved rate</span><strong>{money(selected.rate_amount)} / {human(selected.rate_unit)}</strong></div>
          <div><span>Authorized</span><strong>{selected.authorized_units} {human(selected.rate_unit)}(s)</strong></div>
          <div><span>Already requested</span><strong>{reserved} {human(selected.rate_unit)}(s)</strong></div>
          <div><span>Remaining</span><strong>{remaining} {human(selected.rate_unit)}(s)</strong></div>
        </div> : null}
        <label>Actual units worked
          <input type="number" min="0.01" step="0.01" max={remaining || undefined} value={units} onChange={(event) => setUnits(event.target.value)} required />
        </label>
        <label>Worker note <textarea value={note} onChange={(event) => setNote(event.target.value)} maxLength={1000} placeholder="Optional context for Production review" /></label>
        <div className="vim-pay-safe-note"><strong>Base request preview: {money(preview)}</strong><p>This is the locked Phase 3 base rate × actual units. For W-2 payroll employees, employer-side fringe burden is calculated later after Finance review. Employer fringes are not deductions from your base pay. Employee tax withholding, overtime, reimbursements, deductions and final net pay are not calculated in this phase.</p></div>
        {selected && (access.profile?.engagement_type === 'payroll_employee' || selected.rate_validation_status === 'pending_payroll_rules') ? <div className="vim-pay-safe-note"><strong>Payroll fringe workflow applies</strong><p>Production and Finance review this request first. VIM Payroll then applies the approved union/non-union fringe profile and annual wage-base caps. The request is not sent to a payroll provider in Phase 5.</p></div> : null}
        {message ? <div className="vim-pay-success">{message}</div> : null}
        {error ? <div className="vim-pay-error">{error}</div> : null}
        <button className="vim-pay-primary" type="submit" disabled={submitting || !selected || requested <= 0 || requested > remaining}>{submitting ? 'Submitting…' : 'Submit for Production review'}</button>
      </form>

      <div className="vim-pay-card vim-pay-payment-history">
        <div><span className="vim-pay-kicker">REQUEST HISTORY</span><h2>Approval & payroll status</h2></div>
        {!requests.length ? <p>No payment requests have been submitted yet.</p> : <div className="vim-pay-request-list">
          {requests.map((request) => (
            <article key={request.id} className="vim-pay-request-row">
              <div className="vim-pay-request-main"><strong>{request.project?.name || 'VIM Project'}</strong><span>{request.assignment?.role_name || 'Assignment'} • {request.requested_units} {human(request.rate_unit_snapshot)}(s) • {money(request.base_amount)}</span><small>Submitted {new Date(request.submitted_at).toLocaleDateString()}</small></div>
              <span className={`vim-pay-badge ${statusTone(request.status)}`}>{human(request.status).toUpperCase()}</span>
              <div className="vim-pay-approval-trail">
                {(request.approvals || []).sort((a,b) => new Date(a.reviewed_at) - new Date(b.reviewed_at)).map((approval) => <span key={approval.id}><strong>{human(approval.stage)}:</strong> {human(approval.decision)}{approval.review_note ? ` — ${approval.review_note}` : ''}</span>)}
                {request.status === 'ready_for_payment' ? <span><strong>Finance:</strong> Approved for contractor payment. No funds have been sent yet.</span> : null}
                {request.status === 'payroll_rules_hold' ? <span><strong>Payroll:</strong> Finance reviewed; awaiting fringe profile calculation.</span> : null}
                {request.status === 'payroll_fringe_calculated' ? <span><strong>Payroll:</strong> Employer fringe calculation completed. This is not a net-pay calculation and has not been submitted to a payroll provider.</span> : null}
              </div>
            </article>
          ))}
        </div>}
      </div>
    </section>
  );
}
